import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  addCompetitor, listCompetitors, scanCompetitor, getCompetitorHistory,
  getCompetitiveLandscape, scoreCompanyFinancial, getContractorFinancialScore,
  findInvestmentsNear, getGeoReport, getAllDealScores, getPipelineForecast,
  getDealTips, listABTests, createABTest, getABTestResults,
  getGhostDashboard, runAllGhosts, getGhostSignals, submitGhostFeedback, getGhostAnalytics
} from '../lib/api';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

export default function Intelligence({ lang, tenantId }) {
  const [tab, setTab] = useState('competitors');
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComp, setNewComp] = useState({ name: '', website: '', products_url: '' });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [landscape, setLandscape] = useState(null);

  // Financial
  const [finNip, setFinNip] = useState('');
  const [finResult, setFinResult] = useState(null);

  // Geo
  const [geoAddress, setGeoAddress] = useState('');
  const [geoRadius, setGeoRadius] = useState(50);
  const [geoResult, setGeoResult] = useState(null);

  // Predictive
  const [dealScores, setDealScores] = useState([]);
  const [forecast, setForecast] = useState(null);

  // A/B Testing
  const [abTests, setAbTests] = useState([]);

  // Ghosts
  const [ghostDashboard, setGhostDashboard] = useState(null);
  const [ghostSignals, setGhostSignals] = useState([]);
  const [expandedSignal, setExpandedSignal] = useState(null);
  const [expandedGeo, setExpandedGeo] = useState(null);

  useEffect(() => { loadTab(); }, [tab]);

  async function loadTab() {
    setLoading(true);
    try {
      if (tab === 'competitors') {
        const data = await listCompetitors();
        setCompetitors(data);
      } else if (tab === 'predictive') {
        const [scores, fc] = await Promise.all([getAllDealScores(), getPipelineForecast()]);
        setDealScores(scores?.scores || scores || []);
        setForecast(fc);
      } else if (tab === 'ab_testing') {
        const data = await listABTests();
        setAbTests(data);
      } else if (tab === 'ghosts') {
        const [dashboard, signals] = await Promise.all([
          getGhostDashboard().catch(() => null),
          getGhostSignals({ limit: 30 }).catch(() => []),
        ]);
        setGhostDashboard(dashboard);
        setGhostSignals(signals);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleAddCompetitor() {
    try {
      await addCompetitor(newComp);
      toast.success(t(lang, 'Dodano konkurenta', 'Competitor added'));
      setShowAddDialog(false);
      setNewComp({ name: '', website: '', products_url: '' });
      loadTab();
    } catch (e) { toast.error(e.message); }
  }

  async function handleScan(id) {
    try {
      toast.info(t(lang, 'Skanowanie...', 'Scanning...'));
      const result = await scanCompetitor(id);
      toast.success(t(lang, 'Skan zakończony', 'Scan complete'));
      loadTab();
    } catch (e) { toast.error(e.message); }
  }

  async function handleLandscape() {
    setLoading(true);
    try {
      const data = await getCompetitiveLandscape();
      setLandscape(data);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function handleFinancialScore() {
    if (!finNip) return;
    setLoading(true);
    try {
      const result = await scoreCompanyFinancial({ nip: finNip });
      setFinResult(result);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function handleGeoSearch() {
    if (!geoAddress) return;
    setLoading(true);
    try {
      const result = await findInvestmentsNear({ address: geoAddress, radius_km: geoRadius });
      setGeoResult(result);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function handleGhostFeedback(signalId, feedbackType) {
    try {
      const result = await submitGhostFeedback(signalId, { feedback_type: feedbackType });
      toast.success(t(lang,
        `Feedback zapisany! Trafność ducha: ${(result.accuracy * 100).toFixed(0)}%`,
        `Feedback saved! Ghost accuracy: ${(result.accuracy * 100).toFixed(0)}%`
      ));
      // Update signal in list
      setGhostSignals(prev => prev.map(s =>
        s.signal_id === signalId ? { ...s, feedback_status: feedbackType } : s
      ));
    } catch (e) { toast.error(e.message); }
  }

  async function handleRunGhosts() {
    setLoading(true);
    try {
      toast.info(t(lang, 'Uruchamianie duchów...', 'Running ghosts...'));
      const result = await runAllGhosts();
      toast.success(t(lang, `Duchy wygenerowały ${result.signals_count} sygnałów`, `Ghosts generated ${result.signals_count} signals`));
      loadTab();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  const priorityColor = (p) => {
    if (p === 4 || p === 'CRITICAL') return 'bg-red-600';
    if (p === 3 || p === 'HIGH') return 'bg-orange-500';
    if (p === 2 || p === 'MEDIUM') return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const riskColor = (level) => {
    if (level === 'low') return 'bg-green-500';
    if (level === 'medium') return 'bg-yellow-500';
    if (level === 'high') return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t(lang, 'Centrum Wywiadowcze', 'Intelligence Center')}</h1>
        <p className="text-muted-foreground text-sm">
          {t(lang, 'Konkurencja, finanse, geo-targeting, predykcje, A/B testy', 'Competitors, finances, geo-targeting, predictions, A/B tests')}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="ghosts">{t(lang, 'Duchy', 'Ghosts')}</TabsTrigger>
          <TabsTrigger value="competitors">{t(lang, 'Konkurencja', 'Competitors')}</TabsTrigger>
          <TabsTrigger value="financial">{t(lang, 'Scoring', 'Scoring')}</TabsTrigger>
          <TabsTrigger value="geo">{t(lang, 'Geo', 'Geo')}</TabsTrigger>
          <TabsTrigger value="predictive">{t(lang, 'Predykcje', 'Predictions')}</TabsTrigger>
          <TabsTrigger value="ab_testing">{t(lang, 'A/B Testy', 'A/B Tests')}</TabsTrigger>
        </TabsList>

        {/* GHOSTS TAB */}
        <TabsContent value="ghosts" className="space-y-4">
          <div className="flex gap-2 items-center">
            <Button onClick={handleRunGhosts} disabled={loading}>
              {t(lang, 'Uruchom wszystkie duchy', 'Run all ghosts')}
            </Button>
            {ghostDashboard && (
              <div className="text-sm text-muted-foreground">
                {ghostDashboard.active_ghosts}/{ghostDashboard.total_ghosts} {t(lang, 'aktywnych', 'active')} |{' '}
                {ghostDashboard.total_signals} {t(lang, 'sygnałów', 'signals')} |{' '}
                {ghostDashboard.critical_signals} {t(lang, 'krytycznych', 'critical')}
              </div>
            )}
          </div>

          {/* Ghost statuses */}
          {ghostDashboard?.ghost_statuses && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(ghostDashboard.ghost_statuses).map(([name, st]) => (
                <Card key={name} className="border-l-4" style={{ borderLeftColor: st.last_run ? '#22c55e' : '#6b7280' }}>
                  <CardContent className="pt-3 pb-3">
                    <div className="font-medium text-sm">{name.replace('_ghost', '')}</div>
                    <div className="text-xs text-muted-foreground">
                      {st.signals_last_run || 0} {t(lang, 'sygnałów', 'signals')} | {st.total_runs} {t(lang, 'uruchomień', 'runs')}
                    </div>
                    {st.error_rate > 0.1 && <Badge className="bg-red-500 text-xs mt-1">err: {(st.error_rate * 100).toFixed(0)}%</Badge>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Signals list with expandable details + feedback */}
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t(lang, 'Sygnały', 'Signals')}</h3>
            {ghostSignals.length === 0 && (
              <p className="text-sm text-muted-foreground">{t(lang, 'Brak sygnałów. Uruchom duchy aby wygenerować.', 'No signals. Run ghosts to generate.')}</p>
            )}
            {ghostSignals.map((sig, i) => {
              const isExpanded = expandedSignal === i;
              return (
                <Card key={sig.signal_id || i} className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setExpandedSignal(isExpanded ? null : i)}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={priorityColor(sig.priority)}>{sig.priority === 4 ? 'CRITICAL' : sig.priority === 3 ? 'HIGH' : sig.priority === 2 ? 'MED' : 'LOW'}</Badge>
                          <span className="font-medium">{sig.title}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {sig.source} | {sig.action} | {t(lang, 'pewność', 'conf')}: {((sig.confidence || 0) * 100).toFixed(0)}%
                          {sig.target_name && <> | {sig.target_name}</>}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">{sig.created_at ? new Date(sig.created_at).toLocaleDateString() : ''}</div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t space-y-2">
                        {sig.description && <p className="text-sm">{sig.description}</p>}
                        {sig.target_name && <div className="text-sm"><strong>{t(lang, 'Cel', 'Target')}:</strong> {sig.target_name} {sig.target_id && <span className="text-muted-foreground">({sig.target_id})</span>}</div>}
                        {sig.patterns && sig.patterns.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {sig.patterns.map(p => <Badge key={p} variant="outline" className="text-xs">{p}</Badge>)}
                          </div>
                        )}
                        {sig.ghost_weight && sig.ghost_weight !== 1 && (
                          <div className="text-xs text-muted-foreground">{t(lang, 'Waga ducha', 'Ghost weight')}: {sig.ghost_weight?.toFixed(2)}</div>
                        )}
                        {sig.data && Object.keys(sig.data).length > 0 && (
                          <details className="text-xs" onClick={(e) => e.stopPropagation()}>
                            <summary className="cursor-pointer text-muted-foreground">{t(lang, 'Szczegóły', 'Details')}</summary>
                            <pre className="mt-1 bg-muted p-2 rounded text-xs whitespace-pre-wrap">{JSON.stringify(sig.data, null, 2)}</pre>
                          </details>
                        )}

                        {/* Feedback buttons */}
                        {sig.signal_id && sig.feedback_status === 'pending' && (
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" variant="outline" className="text-green-600 border-green-600"
                              onClick={(e) => { e.stopPropagation(); handleGhostFeedback(sig.signal_id, 'led_to_deal'); }}>
                              {t(lang, 'Deal!', 'Deal!')}
                            </Button>
                            <Button size="sm" variant="outline" className="text-blue-600 border-blue-600"
                              onClick={(e) => { e.stopPropagation(); handleGhostFeedback(sig.signal_id, 'useful'); }}>
                              {t(lang, 'Przydatne', 'Useful')}
                            </Button>
                            <Button size="sm" variant="outline" className="text-orange-600 border-orange-600"
                              onClick={(e) => { e.stopPropagation(); handleGhostFeedback(sig.signal_id, 'irrelevant'); }}>
                              {t(lang, 'Nieistotne', 'Irrelevant')}
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-600"
                              onClick={(e) => { e.stopPropagation(); handleGhostFeedback(sig.signal_id, 'false_positive'); }}>
                              {t(lang, 'Fałszywy', 'False')}
                            </Button>
                          </div>
                        )}
                        {sig.feedback_status && sig.feedback_status !== 'pending' && (
                          <Badge variant="outline" className="text-xs">{t(lang, 'Oceniono', 'Rated')}: {sig.feedback_status}</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* COMPETITORS TAB */}
        <TabsContent value="competitors" className="space-y-4">
          <div className="flex gap-2">
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>{t(lang, '+ Dodaj Konkurenta', '+ Add Competitor')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t(lang, 'Dodaj konkurenta do monitoringu', 'Add competitor to monitor')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input placeholder={t(lang, 'Nazwa firmy', 'Company name')} value={newComp.name}
                    onChange={e => setNewComp({ ...newComp, name: e.target.value })} />
                  <Input placeholder="https://www.competitor.com" value={newComp.website}
                    onChange={e => setNewComp({ ...newComp, website: e.target.value })} />
                  <Input placeholder={t(lang, 'URL produktów (opcja)', 'Products URL (optional)')} value={newComp.products_url}
                    onChange={e => setNewComp({ ...newComp, products_url: e.target.value })} />
                  <Button onClick={handleAddCompetitor} className="w-full">{t(lang, 'Dodaj', 'Add')}</Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={handleLandscape} disabled={loading}>
              {t(lang, 'Analiza Rynku', 'Market Analysis')}
            </Button>
          </div>

          {competitors.map(c => (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{c.name}</CardTitle>
                  <div className="flex gap-2">
                    <Badge variant="outline">{c.website}</Badge>
                    <Button size="sm" onClick={() => handleScan(c.id)}>
                      {t(lang, 'Skanuj', 'Scan')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {c.last_scan && (
                  <div className="text-sm text-muted-foreground">
                    {t(lang, 'Ostatni skan', 'Last scan')}: {new Date(c.last_scan).toLocaleString()}
                    {c.changes_detected > 0 && (
                      <Badge className="ml-2 bg-orange-500">{c.changes_detected} {t(lang, 'zmian', 'changes')}</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {landscape && (
            <Card>
              <CardHeader><CardTitle>{t(lang, 'Analiza Krajobrazu Konkurencyjnego', 'Competitive Landscape Analysis')}</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-sm whitespace-pre-wrap bg-muted p-4 rounded">{JSON.stringify(landscape, null, 2)}</pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* FINANCIAL SCORING TAB */}
        <TabsContent value="financial" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t(lang, 'Scoring Finansowy Kontrahenta', 'Financial Health Scoring')}</CardTitle>
              <CardDescription>{t(lang, 'Analiza wypłacalności na podstawie KRS, rejestr.io i lex-lex.com', 'Solvency analysis from KRS, rejestr.io and lex-lex.com')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input placeholder="NIP (np. 1234567890)" value={finNip} onChange={e => setFinNip(e.target.value)} className="max-w-xs" />
                <Button onClick={handleFinancialScore} disabled={loading || !finNip}>
                  {t(lang, 'Sprawdź', 'Check')}
                </Button>
              </div>

              {finResult && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl font-bold">{finResult.score || finResult.overall_score || '?'}</div>
                      <div className="text-sm text-muted-foreground">/100</div>
                      <Badge className={`mt-2 ${riskColor(finResult.risk_level)}`}>
                        {finResult.risk_level?.toUpperCase()}
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm font-medium">{t(lang, 'Limit kredytowy', 'Credit Limit')}</div>
                      <div className="text-2xl font-bold">{finResult.credit_limit ? `${finResult.credit_limit.toLocaleString()} PLN` : 'N/A'}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm font-medium">{t(lang, 'Ryzyko płatnicze', 'Payment Risk')}</div>
                      <div className="text-2xl font-bold">{finResult.payment_risk || 'N/A'}%</div>
                    </CardContent>
                  </Card>
                  {finResult.details && (
                    <div className="col-span-3">
                      <pre className="text-sm whitespace-pre-wrap bg-muted p-4 rounded">{JSON.stringify(finResult.details, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GEO-TARGETING TAB */}
        <TabsContent value="geo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t(lang, 'Geo-Targeting Inwestycji', 'Investment Geo-Targeting')}</CardTitle>
              <CardDescription>{t(lang, 'Znajdź pozwolenia budowlane i inwestycje w okolicy', 'Find building permits and investments nearby')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input placeholder={t(lang, 'Adres (np. Warszawa, ul. Marszałkowska)', 'Address')}
                  value={geoAddress} onChange={e => setGeoAddress(e.target.value)} className="flex-1" />
                <Input type="number" placeholder="km" value={geoRadius} onChange={e => setGeoRadius(e.target.value)} className="w-20" />
                <Button onClick={handleGeoSearch} disabled={loading || !geoAddress}>
                  {t(lang, 'Szukaj', 'Search')}
                </Button>
              </div>

              {geoResult && (
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    {t(lang, `Znaleziono ${geoResult.investments?.length || 0} inwestycji w promieniu ${geoResult.radius_km}km`,
                      `Found ${geoResult.investments?.length || 0} investments within ${geoResult.radius_km}km`)}
                  </div>
                  {(geoResult.investments || []).map((inv, i) => {
                    const isExpGeo = expandedGeo === i;
                    return (
                      <Card key={i} className="cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => setExpandedGeo(isExpGeo ? null : i)}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-medium">{inv.investor_name || inv.investor || inv.title || `Investment #${i + 1}`}</div>
                                {inv.opportunity_score >= 0.8 && <Badge className="bg-red-600 text-xs">HOT</Badge>}
                              </div>
                              <div className="text-sm text-muted-foreground">{inv.city || inv.address || inv.location}</div>
                              <div className="text-sm">{inv.category_name || inv.category} {inv.area_m2 ? `| ${inv.area_m2.toLocaleString()} m²` : ''}</div>
                              {(inv.description || inv.detailed_description) && (
                                <div className="text-xs text-muted-foreground/90 line-clamp-2 mt-1 leading-snug">
                                  {inv.description || inv.detailed_description}
                                </div>
                              )}
                            </div>
                            <Badge variant="outline">{inv.distance_km?.toFixed(1) || '?'} km</Badge>
                          </div>

                          {/* Expanded details */}
                          {isExpGeo && (
                            <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                              {inv.description && <div><strong>{t(lang, 'Typ', 'Type')}:</strong> {inv.description}</div>}
                              {inv.detailed_description && <p className="text-muted-foreground">{inv.detailed_description}</p>}
                              {inv.designer_name && <div><strong>{t(lang, 'Projektant (wniosek GUNB)', 'Designer (GUNB permit)')}:</strong> {inv.designer_name} {inv.designer_license && <span className="text-muted-foreground">({inv.designer_license})</span>} <span className="ml-1 text-[10px] text-amber-300/80 cursor-help" title={t(lang, 'Dane z rejestru GUNB RWDZ — projektant wskazany we wniosku o pozwolenie. Może różnić się od finalnego zespołu (nadzór, wykonawcy). GUNB nie publikuje wykonawców.', 'GUNB RWDZ registry data — designer named on the permit application. May differ from the final team; GUNB does not publish contractors.')}>ⓘ {t(lang, 'dane rejestrowe', 'registry data')}</span></div>}
                              {inv.kubatura && <div><strong>{t(lang, 'Kubatura', 'Volume')}:</strong> {inv.kubatura} m³</div>}
                              {inv.permit_date && <div><strong>{t(lang, 'Data pozwolenia', 'Permit date')}:</strong> {inv.permit_date}</div>}
                              {inv.organ && <div><strong>{t(lang, 'Organ', 'Authority')}:</strong> {inv.organ}</div>}
                              {inv.voivodeship && <div><strong>{t(lang, 'Województwo', 'Voivodeship')}:</strong> {inv.voivodeship}</div>}
                              <div className="flex gap-2 pt-2">
                                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); toast.info(t(lang, 'Wkrótce: dodaj do pipeline', 'Coming: add to pipeline')); }}>
                                  {t(lang, 'Dodaj do pipeline', 'Add to pipeline')}
                                </Button>
                                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); toast.info(t(lang, 'Wkrótce: szukaj kontakt', 'Coming: find contact')); }}>
                                  {t(lang, 'Szukaj kontakt', 'Find contact')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREDICTIVE SCORING TAB */}
        <TabsContent value="predictive" className="space-y-4">
          {forecast && (
            <Card>
              <CardHeader>
                <CardTitle>{t(lang, 'Prognoza Pipeline', 'Pipeline Forecast')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {(forecast.monthly_forecast || []).map((m, i) => (
                    <Card key={i}>
                      <CardContent className="pt-4 text-center">
                        <div className="text-sm text-muted-foreground">{m.month}</div>
                        <div className="text-2xl font-bold">{(m.weighted_value || 0).toLocaleString()} PLN</div>
                        <div className="text-sm">{m.deal_count || 0} {t(lang, 'dealów', 'deals')}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {forecast.total_weighted && (
                  <div className="mt-4 text-center text-lg font-semibold">
                    {t(lang, 'Łączna prognoza', 'Total forecast')}: {forecast.total_weighted.toLocaleString()} PLN
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>{t(lang, 'Scoring Dealów', 'Deal Scoring')}</CardTitle></CardHeader>
            <CardContent>
              {Array.isArray(dealScores) && dealScores.length > 0 ? (
                <div className="space-y-2">
                  {dealScores.map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <div className="font-medium">{d.title || d.deal_id}</div>
                        <div className="text-sm text-muted-foreground">{d.contractor_name}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-bold">{d.probability || d.score || 0}%</div>
                          <div className="text-xs text-muted-foreground">{d.predicted_close_date || ''}</div>
                        </div>
                        <Badge className={d.probability > 70 ? 'bg-green-500' : d.probability > 40 ? 'bg-yellow-500' : 'bg-red-500'}>
                          {d.probability > 70 ? t(lang, 'Gorący', 'Hot') : d.probability > 40 ? t(lang, 'Ciepły', 'Warm') : t(lang, 'Zimny', 'Cold')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">{t(lang, 'Brak aktywnych dealów do scoringu', 'No active deals to score')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* A/B TESTING TAB */}
        <TabsContent value="ab_testing" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => toast.info(t(lang, 'Wkrótce: kreator testów A/B', 'Coming: A/B test creator'))}>
              {t(lang, '+ Nowy Test A/B', '+ New A/B Test')}
            </Button>
          </div>

          {abTests.length > 0 ? abTests.map(test => (
            <Card key={test.id}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">{test.name}</CardTitle>
                  <Badge variant={test.status === 'completed' ? 'default' : 'outline'}>{test.status}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>{t(lang, 'Warianty', 'Variants')}: {test.variants?.length || 0}</div>
                  <div>{t(lang, 'Wysłano', 'Sent')}: {test.sent_count || 0}</div>
                  <div>{t(lang, 'Zwycięzca', 'Winner')}: {test.winner || '-'}</div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                {t(lang, 'Brak testów A/B. Stwórz pierwszy test aby zacząć optymalizować maile.', 'No A/B tests yet. Create your first test to start optimizing emails.')}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
